import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { BookingDetailPanel } from '../components/BookingDetailPanel';
import { AppShell, useShell } from '../components/AppShell';
import {
  hoursForDate,
  applyDateException,
  formatWallMin,
} from '../lib/availability';
import {
  startOfDay,
  endOfDay,
  addDays,
  startOfWeekMon,
  fmtDay,
  fmtMoney,
  dateKeyOf,
  parseIsoMin,
  DEFAULT_DAY_START_HR,
  DEFAULT_DAY_END_HR,
  ROW_HEIGHT,
  PX_PER_MIN,
  MIN_CARD_H,
  CARD_INSET_PX,
  owingFor,
} from '../lib/diary/helpers';
import { ByBarberView } from '../components/diary/views/ByBarberView';
import { DayView } from '../components/diary/views/DayView';
import { TodayView } from '../components/diary/views/TodayView';
import { WeekView } from '../components/diary/views/WeekView';
import { NewBookingModal } from '../components/diary/NewBookingModal';

// ============================================================
// Diary — day / by-barber / week / today views
// Container. View components live in components/diary/*,
// pure helpers in lib/diary/helpers.js. See those modules for
// the payment-pill logic, colour rules, grid math and ghost-slot
// generation that used to live inline here.
// ============================================================

const DiaryInner = () => {
  const { profile } = useShell();
  const isBarber = profile?.role === 'barber';
  // Per-barber capability flags. `undefined` while barbers are still
  // loading — treated by useBookingActions as "no perms known, hide
  // nothing" for shop_admin / master_admin. For a barber caller we
  // populate below from their own barbers row and pass true/false
  // down so the StatusMenu / PaymentActionSheet can gate items.
  const [myPerms, setMyPerms] = React.useState({});
  // Deep-link: /diary?date=YYYY-MM-DD&booking=<id> — used by Dashboard
  // "Today's chair" rows so a click drills straight into the day view.
  const initialDate = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search).get('date');
      if (p && /^\d{4}-\d{2}-\d{2}$/.test(p)) return startOfDay(new Date(p + 'T00:00:00'));
    }
    return startOfDay(new Date());
  }, []);
  const focusBookingId = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('booking');
    }
    return null;
  }, []);
  const [date, setDate]         = useState(initialDate);
  const [view, setView]         = useState(() => {
    // Mobile-first: pick Today queue on narrow screens so barbers can
    // work from their phone without ever seeing the desktop grid.
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches) return 'today';
    return 'by-barber';
  });
  const [barbers, setBarbers]   = useState([]);
  const [hoursByBarber, setHoursByBarber] = useState({});
  // Date-specific overlays that override or subtract from weekly hours.
  // Shape:
  //   exceptionsByBarber = { [barberId]: { [YYYY-MM-DD]: { is_closed, start_time, end_time } } }
  //   breaksByBarber     = { [barberId]: { [YYYY-MM-DD]: [ { start_time, end_time, label } ] } }
  // Loaded per visible date range inside loadBookings — refreshed by the
  // same 30-s poller so a barber toggling a lunch break on their phone
  // shows up on the shop admin's Diary within 30 s.
  const [exceptionsByBarber, setExceptionsByBarber] = useState({});
  const [breaksByBarber, setBreaksByBarber]         = useState({});
  const [bookings, setBookings] = useState([]);
  const [firstBookingByCustomer, setFirstBookingByCustomer] = useState({});
  const [loading, setLoading]   = useState(true);

  // When arriving via /diary?booking=<id>, scroll to and pulse the card.
  React.useEffect(() => {
    if (!focusBookingId || loading || !bookings.length) return;
    // Wait a frame for the render to settle, then scroll.
    const t = setTimeout(() => {
      const els = document.querySelectorAll(
        `[data-testid="ev-${focusBookingId}"], [data-testid="today-row-${focusBookingId}"]`
      );
      const el = els[0];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [focusBookingId, loading, bookings.length, view]);
  const [showNew, setShowNew]   = useState(false);
  const [newPrefill, setNewPrefill] = useState(null);
  // Booking detail panel — one instance shared by all four views. Clicking
  // any `.ev` card in Today/Day/By-barber/Week sets this to the booking id.
  const [openBookingId, setOpenBookingId] = useState(null);
  // Barber-scoping toggle. Default ON for barbers (show only their own
  // bookings + lane); tap to override for shift coverage.
  const [scopeToSelf, setScopeToSelf] = useState(isBarber);
  const [myBarberId, setMyBarberId] = useState(null);

  const shopId = profile?.shop_id;

  useEffect(() => {
    if (!shopId) return;
    (async () => {
      const { data: bb } = await supabase
        .from('barbers').select('id,name,role,user_id,slot_interval_minutes,booking_buffer_minutes,can_manage_bookings,can_take_payments')
        .eq('shop_id', shopId).eq('active', true).order('created_at');
      const all = bb || [];

      // Locate this signed-in barber's row so we can (a) scope views to
      // themselves and (b) filter their own barber_id in mutations.
      let mineId = null;
      if (isBarber && profile?.id) {
        const mine = all.find(x => x.user_id === profile.id);
        mineId = mine?.id || null;
        setMyBarberId(mineId);
        // Also stash their capability flags so the diary UI can hide
        // status / payment actions when the barber lacks them.
        setMyPerms({
          canManageBookings: !!mine?.can_manage_bookings,
          canTakePayments:   !!mine?.can_take_payments,
        });
      }

      const visible = (isBarber && scopeToSelf && mineId)
        ? all.filter(x => x.id === mineId)
        : all;
      setBarbers(visible);

      if (all.length) {
        const { data: hh } = await supabase
          .from('barber_hours').select('barber_id,weekday,start_time,end_time,is_open')
          .in('barber_id', all.map(x => x.id));
        const map = {};
        (hh || []).forEach(r => {
          if (!map[r.barber_id]) map[r.barber_id] = [];
          map[r.barber_id].push(r);
        });
        setHoursByBarber(map);
      }
    })();
  }, [shopId, scopeToSelf, isBarber, profile?.id]);

  useEffect(() => {
    if (!shopId) return;
    loadBookings(shopId, date, view);
  }, [shopId, date, view, scopeToSelf, myBarberId, barbers.length]);

  // Batch 3.3 — Diary/Schedule sync. Poll every 30 s while the tab is
  // visible so a booking made by another admin, or a break added by a
  // barber, shows up automatically without the user hitting refresh.
  // Pauses when the tab is hidden to spare Supabase requests.
  useEffect(() => {
    if (!shopId) return undefined;
    let disposed = false;
    const tick = () => {
      if (disposed || document.hidden) return;
      loadBookings(shopId, date, view);
    };
    const iv = setInterval(tick, 30_000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      disposed = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [shopId, date, view, scopeToSelf, myBarberId]);

  const loadBookings = async (sid, d, v) => {
    setLoading(true);
    let from, to;
    if (v === 'week') {
      from = startOfWeekMon(d);
      to   = endOfDay(addDays(from, 6));
    } else {
      from = startOfDay(d);
      to   = endOfDay(d);
    }
    let q = supabase
      .from('bookings')
      .select('id,shop_id,starts_at,ends_at,status,channel,deposit_paid,deposit_amount,paid_in_full,payment_status,balance_paid_method,balance_paid_amount,barber_id,customer_id,stripe_payment_intent_id,customer:customers(name,phone),service:services(name,price,duration_min,deposit_mode),barber:barbers(name)')
      .eq('shop_id', sid)
      .gte('starts_at', from.toISOString())
      .lte('starts_at', to.toISOString())
      .order('starts_at');
    if (isBarber && scopeToSelf && myBarberId) {
      q = q.eq('barber_id', myBarberId);
    }
    const { data } = await q;
    const rows = data || [];
    setBookings(rows);

    // ---- Overlays: date-exceptions + breaks for every barber in scope ----
    // Fetched for the same visible date range so we cover Day, By-barber
    // and Week views without a second round-trip when the poller ticks.
    const barberIds = barbers.map(b => b.id).filter(Boolean);
    if (barberIds.length) {
      const fromKey = dateKeyOf(from);
      const toKey   = dateKeyOf(to);
      const [{ data: excRows }, { data: brkRows }] = await Promise.all([
        supabase.from('barber_date_exceptions')
          .select('barber_id,date,is_closed,start_time,end_time')
          .in('barber_id', barberIds)
          .gte('date', fromKey).lte('date', toKey),
        supabase.from('barber_breaks')
          .select('barber_id,date,start_time,end_time,label')
          .in('barber_id', barberIds)
          .gte('date', fromKey).lte('date', toKey),
      ]);
      const excMap = {};
      (excRows || []).forEach(r => {
        excMap[r.barber_id] = excMap[r.barber_id] || {};
        excMap[r.barber_id][r.date] = r;
      });
      setExceptionsByBarber(excMap);
      const brkMap = {};
      (brkRows || []).forEach(r => {
        brkMap[r.barber_id] = brkMap[r.barber_id] || {};
        (brkMap[r.barber_id][r.date] = brkMap[r.barber_id][r.date] || []).push(r);
      });
      setBreaksByBarber(brkMap);
    } else {
      setExceptionsByBarber({});
      setBreaksByBarber({});
    }

    const cids = Array.from(new Set(rows.map(r => r.customer_id)));
    if (cids.length) {
      const { data: firsts } = await supabase
        .from('bookings')
        .select('customer_id,starts_at')
        .eq('shop_id', sid)
        .in('customer_id', cids)
        .order('starts_at', { ascending: true });
      const map = {};
      (firsts || []).forEach(r => { if (!map[r.customer_id]) map[r.customer_id] = r.starts_at; });
      setFirstBookingByCustomer(map);
    } else {
      setFirstBookingByCustomer({});
    }
    setLoading(false);
  };

  const depositsTotal = useMemo(() =>
    bookings
      .filter(b => b.payment_status === 'succeeded' || b.deposit_paid)
      .reduce((s, b) => s + Number(b.deposit_amount || 0), 0),
    [bookings]
  );

  const owingTotal = useMemo(() =>
    bookings
      .filter(b => !['cancelled'].includes(b.status))
      .reduce((s, b) => s + owingFor(b), 0),
    [bookings]
  );

  // Dynamic day grid bounds: union of every visible barber's working
  // hours for the picked date (Day / By-barber views). Falls back to
  // 09:00–20:00 when nobody is open. Padded by ±1 hour so booking
  // cards on the edges aren't clipped against the top/bottom rail.
  const { dayStartHr, dayEndHr } = useMemo(() => {
    if (view === 'week' || !barbers.length) {
      return { dayStartHr: DEFAULT_DAY_START_HR, dayEndHr: DEFAULT_DAY_END_HR };
    }
    const dKey = dateKeyOf(date);
    let sMin = null, eMin = null;
    barbers.forEach(b => {
      const weekly = hoursForDate(hoursByBarber, b.id, date);
      const exc = exceptionsByBarber?.[b.id]?.[dKey];
      const h = applyDateException(weekly, exc);
      if (!h.open) return;
      if (sMin === null || h.startMin < sMin) sMin = h.startMin;
      if (eMin === null || h.endMin   > eMin) eMin = h.endMin;
    });
    // Also expand to cover any booking that already lives outside the
    // working window (e.g. an admin-created overrun) so cards never
    // sit invisibly above/below the grid.
    bookings.forEach(bk => {
      const bs = parseIsoMin(bk.starts_at);
      const be = parseIsoMin(bk.ends_at);
      if (sMin === null || bs < sMin) sMin = bs;
      if (eMin === null || be > eMin) eMin = be;
    });
    if (sMin === null || eMin === null) {
      return { dayStartHr: DEFAULT_DAY_START_HR, dayEndHr: DEFAULT_DAY_END_HR };
    }
    return {
      dayStartHr: Math.max(0,  Math.floor(sMin / 60)),
      dayEndHr:   Math.min(24, Math.ceil(eMin / 60)),
    };
  }, [view, barbers, hoursByBarber, exceptionsByBarber, date, bookings]);

  const hours = useMemo(() => {
    const arr = [];
    for (let h = dayStartHr; h < dayEndHr; h++) arr.push(`${String(h).padStart(2,'0')}:00`);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayStartHr, dayEndHr]);

  // Row expansion for legibility. A short booking (e.g. 15 min) would
  // naturally render as a ~15px card — impossible to fit the three
  // required lines (time / name / service). For each booking, we
  // expand EVERY hour it touches to at least the ppm ratio that
  // guarantees the whole card spans MIN_CARD_H + INSET pixels. A
  // booking straddling two hours (e.g. 09:45 → 10:15) therefore
  // stretches both hours — otherwise only its 15-min slice in the
  // start hour would receive the extra pixels and the other 15 min
  // would fall back to the default 62 px/hr scale, chopping the
  // card in half. Hours untouched by any short booking stay at the
  // default ROW_HEIGHT. Time labels below an expanded hour then
  // shift down by exactly the expansion via cumulative `rowTops`.
  const grid = useMemo(() => {
    const rowCount = Math.max(0, dayEndHr - dayStartHr);
    const rowHeights = Array.from({ length: rowCount }, () => ROW_HEIGHT);
    bookings.forEach(b => {
      if (b.status === 'cancelled') return;
      const sMin = parseIsoMin(b.starts_at);
      const eMin = parseIsoMin(b.ends_at);
      const dur = eMin - sMin;
      if (dur <= 0) return;
      // Natural rendered height at the default per-minute scale.
      const naturalRendered = dur * PX_PER_MIN - CARD_INSET_PX;
      if (naturalRendered >= MIN_CARD_H) return;
      // Minimum row height (px/hr) such that any slice of this
      // booking, no matter how short, still gets its proportional
      // share of MIN_CARD_H. Setting every touched hour to this
      // ratio guarantees the total card span is exactly
      // MIN_CARD_H + INSET even when the booking straddles hours.
      const needed = Math.ceil((MIN_CARD_H + CARD_INSET_PX) * 60 / dur);
      const startHourIdx = Math.floor(sMin / 60) - dayStartHr;
      // A booking ending exactly on an hour boundary (e.g. 10:00)
      // doesn't visually enter that hour — step back one minute
      // to find the last hour it actually paints inside.
      const endHourIdx = Math.floor(Math.max(sMin, eMin - 1) / 60) - dayStartHr;
      const from = Math.max(0, startHourIdx);
      const to   = Math.min(rowCount - 1, endHourIdx);
      for (let i = from; i <= to; i++) {
        if (needed > rowHeights[i]) rowHeights[i] = needed;
      }
    });
    const rowTops = [];
    let acc = 0;
    for (let i = 0; i < rowCount; i++) {
      rowTops.push(acc);
      acc += rowHeights[i];
    }
    return { rowHeights, rowTops, totalHeight: acc };
  }, [bookings, dayStartHr, dayEndHr]);

  // Convert a wall-clock minute-of-day to a pixel offset from the top
  // of the grid. Within an expanded hour, minutes still scale linearly
  // against THAT hour's (possibly-expanded) row height, so a booking
  // that starts halfway through an expanded hour still sits at that
  // hour's mid-point. Values outside the day-range clamp cleanly.
  const topForMin = useMemo(() => (min) => {
    if (min <= dayStartHr * 60) return 0;
    if (min >= dayEndHr * 60)   return grid.totalHeight;
    const hourIdx = Math.floor(min / 60) - dayStartHr;
    if (hourIdx < 0 || hourIdx >= grid.rowTops.length) return 0;
    const offsetInHour = min - (hourIdx + dayStartHr) * 60;
    return grid.rowTops[hourIdx] + (offsetInHour / 60) * grid.rowHeights[hourIdx];
  }, [grid, dayStartHr, dayEndHr]);

  const eventStyle = (b) => {
    const sMin = parseIsoMin(b.starts_at);
    const eMin = parseIsoMin(b.ends_at);
    const top = topForMin(sMin);
    const bottom = topForMin(eMin);
    // Card's true pixel span represents the full booked duration
    // (`bottom - top`); the CARD_INSET_PX subtracted here is purely
    // cosmetic — leaves a hairline gap so two back-to-back bookings
    // don't visually merge. The following card's own top edge is
    // still calculated from ITS start time (not stacked below this
    // one), so no time gets swallowed.
    const height = Math.max(20, bottom - top - CARD_INSET_PX);
    return { top: `${top}px`, height: `${height}px` };
  };

  const isFirstForCustomer = (b) => {
    const first = firstBookingByCustomer[b.customer_id];
    return first && new Date(first).getTime() === new Date(b.starts_at).getTime();
  };

  const openNewBooking = (prefill = null) => {
    setNewPrefill(prefill);
    setShowNew(true);
  };

  const showNowLine = startOfDay(date).getTime() === startOfDay(new Date()).getTime();
  const nowTop = useMemo(() => {
    if (!showNowLine) return null;               // hide on non-today dates
    const n = new Date();
    const min = n.getHours() * 60 + n.getMinutes();
    if (min < dayStartHr * 60 || min > dayEndHr * 60) return null;
    return topForMin(min);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNowLine, dayStartHr, dayEndHr, topForMin]);

  // Auto-scroll the page so the current hour is near the top of the
  // grid on first render / view change / date change, WITHOUT pushing
  // the Diary toolbar / legend off-screen. We measure the target `.hr`
  // (or top-of-day for non-today dates) and scroll the window so the
  // target sits ~180px below the viewport top — leaves room for the
  // toolbar + view switcher + legend.
  const calScrollRef = React.useRef(null);
  useEffect(() => {
    if (view === 'today' || view === 'week') return;
    if (loading) return;
    const el = calScrollRef.current;
    if (!el) return;
    const n = new Date();
    const isToday = startOfDay(date).getTime() === startOfDay(n).getTime();
    const targetHr = isToday ? n.getHours() : dayStartHr;
    const t = setTimeout(() => {
      try {
        const hrs = el.querySelectorAll('.hours .hr');
        const idx = Math.max(0, Math.min(hrs.length - 1, targetHr - dayStartHr));
        const target = hrs[idx];
        if (!target) return;
        const rect = target.getBoundingClientRect();
        // Skip if target already comfortably in view.
        if (rect.top > 180 && rect.top < window.innerHeight - 60) return;
        // Absolute page Y of the target; scroll so target ends up
        // ~180px from viewport top (below the toolbar + legend).
        const targetPageY = rect.top + window.scrollY;
        const desiredY = Math.max(0, targetPageY - 180);
        window.scrollTo({ top: desiredY, behavior: 'auto' });
      } catch (e) { console.debug('[diary] auto-scroll to current hour failed:', e); }
    }, 80);
    return () => clearTimeout(t);
  }, [view, date, dayStartHr, loading]);

  const headerLabel = view === 'week'
    ? `Wk ${fmtDay(startOfWeekMon(date))}`
    : fmtDay(date);

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Diary</h2>
          <div className="sub">{profile?.shops?.name} · {barbers.length} barber{barbers.length === 1 ? '' : 's'}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {isBarber && (
            <label className="scope-toggle" title="Show every barber's bookings instead of only yours">
              <input
                type="checkbox"
                checked={!scopeToSelf}
                onChange={(e) => setScopeToSelf(!e.target.checked)}
                data-testid="diary-scope-all-barbers"
              />
              <span>Show all barbers</span>
            </label>
          )}
          <button className="btn s" onClick={() => setDate(startOfDay(new Date()))} data-testid="diary-today">Today</button>
          {!isBarber && (
            <button className="btn p" onClick={() => openNewBooking()} data-testid="diary-new-booking">+ New booking</button>
          )}
        </div>
      </div>

      <div className="body">
        <div className="diary-top">
          <div className="daynav">
            <button onClick={() => setDate(addDays(date, view === 'week' ? -7 : -1))} data-testid="diary-prev">‹</button>
            <span className="lbl" data-testid="diary-label">{headerLabel}</span>
            <button onClick={() => setDate(addDays(date, view === 'week' ? 7 : 1))} data-testid="diary-next">›</button>
          </div>
          <div className="seg" data-testid="diary-views">
            <button className={view === 'today' ? 'on' : ''} onClick={() => setView('today')} data-testid="view-today">Today</button>
            <button className={view === 'day' ? 'on' : ''} onClick={() => setView('day')} data-testid="view-day">Day</button>
            <button className={view === 'by-barber' ? 'on' : ''} onClick={() => setView('by-barber')} data-testid="view-by-barber">By barber</button>
            <button className={view === 'week' ? 'on' : ''} onClick={() => setView('week')} data-testid="view-week">Week</button>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--mute)' }} data-testid="diary-toolbar-totals">
            {bookings.length} booked ·{' '}
            <b style={{ color: 'var(--ink)' }}>{fmtMoney(depositsTotal)}</b> deposits
            {owingTotal > 0 && (
              <>
                {' · '}
                <b style={{ color: '#b91c1c' }} data-testid="diary-owing-total">{fmtMoney(owingTotal)}</b> owing
              </>
            )}
          </div>
        </div>

        <div className="diary-legend" data-testid="diary-legend" aria-label="Booking card colour legend">
          <span className="diary-legend-item"><span className="diary-legend-sw sw-blue" /> Online booking</span>
          <span className="diary-legend-item"><span className="diary-legend-sw sw-teal" /> Walk-in / manual</span>
          <span className="diary-legend-item"><span className="diary-legend-sw sw-new" /> First-time customer</span>
          <span className="diary-legend-item"><span className="diary-legend-sw sw-amber" /> No-show</span>
          <span className="diary-legend-item"><span className="diary-legend-sw sw-red" /> Cancelled</span>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : view === 'today' ? (
          <TodayView
            bookings={bookings}
            isFirstForCustomer={isFirstForCustomer}
            onStatusChanged={() => loadBookings(shopId, date, view)}
            onOpenBooking={setOpenBookingId}
            isBarber={isBarber}
            myBarberId={myBarberId}
            myPerms={myPerms}
            focusBookingId={focusBookingId}
          />
        ) : view === 'by-barber' ? (
          <ByBarberView
            date={date}
            barbers={barbers}
            hoursByBarber={hoursByBarber}
            exceptionsByBarber={exceptionsByBarber}
            breaksByBarber={breaksByBarber}
            bookings={bookings}
            hours={hours}
            rowHeights={grid.rowHeights}
            totalHeight={grid.totalHeight}
            topForMin={topForMin}
            scrollRef={calScrollRef}
            eventStyle={eventStyle}
            isFirstForCustomer={isFirstForCustomer}
            nowTop={nowTop}
            onStatusChanged={() => loadBookings(shopId, date, view)}
            onOpenBooking={setOpenBookingId}
            isBarber={isBarber}
            myBarberId={myBarberId}
            myPerms={myPerms}
            focusBookingId={focusBookingId}
            onClickGhostSlot={(barberId, startMin) =>
              openNewBooking({
                barber_id: barberId,
                date: date.toISOString().slice(0, 10),
                time: formatWallMin(startMin),
              })
            }
          />
        ) : view === 'day' ? (
          <DayView
            barbers={barbers}
            bookings={bookings}
            hours={hours}
            rowHeights={grid.rowHeights}
            scrollRef={calScrollRef}
            eventStyle={eventStyle}
            isFirstForCustomer={isFirstForCustomer}
            nowTop={nowTop}
            onStatusChanged={() => loadBookings(shopId, date, view)}
            onOpenBooking={setOpenBookingId}
            isBarber={isBarber}
            myBarberId={myBarberId}
            myPerms={myPerms}
            focusBookingId={focusBookingId}
          />
        ) : (
          <WeekView
            weekStart={startOfWeekMon(date)}
            bookings={bookings}
            isFirstForCustomer={isFirstForCustomer}
            onPickDay={(d) => { setDate(startOfDay(d)); setView('by-barber'); }}
            onOpenBooking={setOpenBookingId}
          />
        )}
      </div>

      {showNew && (
        <NewBookingModal
          shopId={shopId}
          hoursByBarber={hoursByBarber}
          exceptionsByBarber={exceptionsByBarber}
          breaksByBarber={breaksByBarber}
          allBookings={bookings}
          prefill={newPrefill}
          onClose={() => { setShowNew(false); setNewPrefill(null); }}
          onCreated={() => { setShowNew(false); setNewPrefill(null); loadBookings(shopId, date, view); }}
        />
      )}

      <BookingDetailPanel
        bookingId={openBookingId}
        onClose={() => setOpenBookingId(null)}
        onChanged={() => loadBookings(shopId, date, view)}
      />
    </main>
  );
};

const Diary = () => (
  <AppShell>
    <DiaryInner />
  </AppShell>
);

export default Diary;
